import { Content, Header, HeaderLabel, Page } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  createFrontendModule,
  fetchApiRef,
  PageBlueprint,
  useTranslationRef,
} from '@backstage/frontend-plugin-api';
import {
  Button,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import { useEffect, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import {
  oneDark,
  oneLight,
} from 'react-syntax-highlighter/dist/esm/styles/prism';
import { appTranslationRef } from './translations/app';

type DatabaseCredentials = {
  database: string;
  host: string;
  password: string;
  port: number;
  tlsRequired: boolean;
  username: string;
};

const clearAfterMilliseconds = 60_000;

function CodeSnippet({
  label,
  language,
  value,
}: {
  label: string;
  language: 'php' | 'python';
  value: string;
}) {
  const { t } = useTranslationRef(appTranslationRef);
  const theme = useTheme();

  return (
    <Box mt={3}>
      <Typography component="h3" variant="h6">
        {label}
      </Typography>
      <SyntaxHighlighter
        customStyle={{ margin: 0, overflowX: 'auto' }}
        language={language}
        style={theme.palette.type === 'dark' ? oneDark : oneLight}
      >
        {value}
      </SyntaxHighlighter>
      <Button
        color="primary"
        onClick={() => navigator.clipboard.writeText(value)}
        size="small"
      >
        {t('databaseCredentials.copy')}
      </Button>
    </Box>
  );
}

function phpSnippet(credentials: DatabaseCredentials) {
  return `<?php
$pdo = new PDO(
    sprintf(
        'mysql:host=%s;port=${credentials.port};dbname=%s;charset=utf8mb4',
        getenv('MYSQL_HOST'),
        getenv('MYSQL_DATABASE'),
    ),
    getenv('MYSQL_USERNAME'),
    getenv('MYSQL_PASSWORD'),
    [
        PDO::ATTR_ERRMODE => PDO::ERRMODE_EXCEPTION,
        PDO::MYSQL_ATTR_SSL_CA => getenv('MYSQL_SSL_CA'),
        PDO::MYSQL_ATTR_SSL_VERIFY_SERVER_CERT => true,
    ],
);`;
}

function pythonSnippet(credentials: DatabaseCredentials) {
  return `import os

import mysql.connector

connection = mysql.connector.connect(
    host=os.environ['MYSQL_HOST'],
    port=${credentials.port},
    user=os.environ['MYSQL_USERNAME'],
    password=os.environ['MYSQL_PASSWORD'],
    database=os.environ['MYSQL_DATABASE'],
    ssl_ca=os.environ['MYSQL_SSL_CA'],
    ssl_verify_cert=True,
    ssl_verify_identity=True,
)`;
}

function CredentialValue({ label, value }: { label: string; value: string }) {
  const { t } = useTranslationRef(appTranslationRef);

  return (
    <Grid item xs={12} sm={6}>
      <TextField
        fullWidth
        InputProps={{ readOnly: true }}
        label={label}
        value={value}
        variant="outlined"
      />
      <Button
        color="primary"
        onClick={() => navigator.clipboard.writeText(value)}
        size="small"
      >
        {t('databaseCredentials.copy')}
      </Button>
    </Grid>
  );
}

function DatabaseCredentialsPage() {
  const { t } = useTranslationRef(appTranslationRef);
  const fetchApi = useApi(fetchApiRef);
  const [applicationName, setApplicationName] = useState('');
  const [credentials, setCredentials] = useState<DatabaseCredentials>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!credentials) return undefined;

    const timeout = window.setTimeout(
      () => setCredentials(undefined),
      clearAfterMilliseconds,
    );
    return () => window.clearTimeout(timeout);
  }, [credentials]);

  async function revealCredentials() {
    setCredentials(undefined);
    setError(undefined);
    setLoading(true);
    try {
      const response = await fetchApi.fetch(
        '/api/database-credentials/reveal',
        {
          body: JSON.stringify({ name: applicationName }),
          headers: { 'Content-Type': 'application/json' },
          method: 'POST',
        },
      );
      if (!response.ok) {
        throw new Error(await response.text());
      }
      setCredentials(await response.json());
    } catch {
      setError(t('databaseCredentials.error'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Page themeId="tool">
      <Header title={t('databaseCredentials.title')}>
        <HeaderLabel
          label={t('databaseCredentials.headerLabel')}
          value="MySQL"
        />
      </Header>
      <Content>
        <Card>
          <CardContent>
            <Typography paragraph>
              {t('databaseCredentials.description')}
            </Typography>
            <TextField
              fullWidth
              label={t('databaseCredentials.applicationName')}
              onChange={event => setApplicationName(event.target.value)}
              required
              value={applicationName}
              variant="outlined"
            />
            <Button
              color="primary"
              disabled={!applicationName || loading}
              onClick={revealCredentials}
              style={{ marginTop: 16 }}
              variant="contained"
            >
              {t('databaseCredentials.reveal')}
            </Button>
            {error && <Typography color="error">{error}</Typography>}
          </CardContent>
        </Card>
        {credentials && (
          <Card>
            <CardContent>
              <Typography paragraph>
                {t('databaseCredentials.clearNotice')}
              </Typography>
              <Grid container spacing={2}>
                <CredentialValue
                  label={t('databaseCredentials.host')}
                  value={credentials.host}
                />
                <CredentialValue
                  label={t('databaseCredentials.port')}
                  value={String(credentials.port)}
                />
                <CredentialValue
                  label={t('databaseCredentials.database')}
                  value={credentials.database}
                />
                <CredentialValue
                  label={t('databaseCredentials.tls')}
                  value={
                    credentials.tlsRequired
                      ? t('databaseCredentials.required')
                      : t('databaseCredentials.notRequired')
                  }
                />
                <CredentialValue
                  label={t('databaseCredentials.username')}
                  value={credentials.username}
                />
                <CredentialValue
                  label={t('databaseCredentials.password')}
                  value={credentials.password}
                />
              </Grid>
              <Typography>
                {t('databaseCredentials.sslCaDescription')}
              </Typography>
              <CodeSnippet
                label={t('databaseCredentials.phpExample')}
                language="php"
                value={phpSnippet(credentials)}
              />
              <Typography>
                {t('databaseCredentials.phpPrerequisite')}
              </Typography>
              <CodeSnippet
                label={t('databaseCredentials.pythonExample')}
                language="python"
                value={pythonSnippet(credentials)}
              />
              <Typography>
                {t('databaseCredentials.pythonPrerequisite')}
              </Typography>
              <Button onClick={() => setCredentials(undefined)}>
                {t('databaseCredentials.hide')}
              </Button>
            </CardContent>
          </Card>
        )}
      </Content>
    </Page>
  );
}

export const databaseCredentialsPageModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    PageBlueprint.make({
      name: 'database-credentials',
      params: {
        path: '/database-credentials',
        title: 'MySQL',
        loader: async () => <DatabaseCredentialsPage />,
      },
    }),
  ],
});
